import { Col, Row, Spin } from 'antd';
import { updateMachineGeofence } from 'components/MyMachineDetails/state/operation';
import { BUTTON_MODE } from 'components/MyMachineDetails/util/constant';
import { BaseButton } from 'components/UI/BaseButton';
import { MachineMapPin } from 'components/UI/MachineMapPin';
import { MapGeofence } from 'components/UI/MapGeofence';
import { GEOFENCE_ACTION } from 'components/UI/MapGeofence/util/constant';
import PropType from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { OPERATION_STATUS } from 'util/config/constant';
import { formatDate } from 'util/date';
import { getMachineType } from 'util/machine-type';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import * as selector from '../../state/selector';

const MachineLocation = ({ id }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  const [geofenceAction, setGeofenceAction] = useState(GEOFENCE_ACTION.VIEW);
  const [buttonMode, setButtonMode] = useState(BUTTON_MODE.VIEW);

  const dispatch = useDispatch();
  const { machineInfo, updateGeofenceInfo } = useSelector((state) => ({
    machineInfo: selector.selectMachineDetailsInformationInfo(state),
    updateGeofenceInfo: selector.selectUpdateGeofenceInfo(state)
  }));

  const machineLocationLat = machineInfo.data?.location?.latitude;
  const machineLocationLng = machineInfo.data?.location?.longitude;

  const geofenceBounds = useMemo(() => {
    const { northLatitude, eastLongitude, southLatitude, westLongitude } =
      machineInfo.data.location || {};

    if (northLatitude && eastLongitude && southLatitude && westLongitude) {
      return {
        north: parseFloat(northLatitude),
        east: parseFloat(eastLongitude),
        south: parseFloat(southLatitude),
        west: parseFloat(westLongitude)
      };
    }

    return null;
  }, [machineInfo.data?.location]);

  useEffect(() => {
    if (!geofenceBounds) {
      return;
    }

    setGeofenceAction(GEOFENCE_ACTION.INITIAL_GEOFENCE);
  }, [geofenceBounds]);

  useEffect(() => {
    if (updateGeofenceInfo.status === OPERATION_STATUS.SUCCESS) {
      setGeofenceAction(GEOFENCE_ACTION.VIEW);
      setButtonMode(BUTTON_MODE.VIEW);
    }
  }, [updateGeofenceInfo.status]);

  const onFinish = useCallback(
    (data) => {
      dispatch(
        updateMachineGeofence(id, {
          location: {
            northLatitude: data.north,
            eastLongitude: data.east,
            southLatitude: data.south,
            westLongitude: data.west
          }
        })
      );
    },
    [dispatch, id]
  );

  const renderMarker = useCallback(() => {
    if (machineLocationLat && machineLocationLng) {
      return (
        <MachineMapPin
          lat={machineLocationLat}
          lng={machineLocationLng}
          machineType={getMachineType(machineInfo.data.materialNumber)}
          machineAlias={machineInfo.data.alias}
          text={formatDate(machineInfo.data.lastConnectedAt, {
            shortFormat: true
          })}
        />
      );
    }

    return null;
  }, [
    machineInfo.data.alias,
    machineInfo.data.lastConnectedAt,
    machineLocationLat,
    machineLocationLng,
    machineInfo.data.materialNumber
  ]);

  const onClickChangeAllowedArea = useCallback(() => {
    setGeofenceAction(GEOFENCE_ACTION.EDIT);
    setButtonMode(BUTTON_MODE.EDIT);
  }, []);

  const onClickDiscardChanges = useCallback(() => {
    setGeofenceAction(GEOFENCE_ACTION.DISCARD);
    setButtonMode(BUTTON_MODE.VIEW);
  }, []);

  const onClickSaveChanges = useCallback(() => {
    setGeofenceAction(GEOFENCE_ACTION.SAVE);
  }, []);

  const isLoadingWidget = machineInfo.isLoading && !machineInfo.data.id;

  const mapCenterLocation = useMemo(() => {
    if (machineLocationLat && machineLocationLng) {
      return [parseFloat(machineLocationLat), parseFloat(machineLocationLng)];
    }

    return undefined;
  }, [machineLocationLat, machineLocationLng]);

  return (
    <div className="machine-location-details widget">
      <Row className="machine-location-header widget-header">
        <Col lg={24} xl={12} className="sub-title machine-location-title">
          <span>{t('machineLocationPanel.title')}</span>

          {buttonMode === BUTTON_MODE.EDIT && (
            <BaseButton
              className="machine-location-btn discard-btn"
              disabled={updateGeofenceInfo.isLoading}
              onClick={onClickDiscardChanges}>
              {t('machineLocationPanel.discardButton')}
            </BaseButton>
          )}
        </Col>

        <Col lg={24} xl={12} className="machine-location-action">
          {buttonMode === BUTTON_MODE.VIEW && (
            <BaseButton
              className="machine-location-btn change-allowed-btn"
              disabled={isLoadingWidget}
              onClick={onClickChangeAllowedArea}>
              {t('machineLocationPanel.changeButton')}
            </BaseButton>
          )}

          {buttonMode === BUTTON_MODE.EDIT && (
            <BaseButton
              className="base-yellow-btn machine-location-btn save-all-btn"
              loading={updateGeofenceInfo.isLoading}
              onClick={onClickSaveChanges}>
              {t('machineLocationPanel.saveButton')}
            </BaseButton>
          )}
        </Col>
      </Row>

      <Spin spinning={isLoadingWidget}>
        <div className="map">
          <MapGeofence
            actionName={geofenceAction}
            onSave={onFinish}
            renderMarker={renderMarker}
            geofenceBounds={geofenceBounds}
            mapCenterLocation={mapCenterLocation}
          />
        </div>
      </Spin>
    </div>
  );
};

MachineLocation.propTypes = {
  id: PropType.string.isRequired
};

export default MachineLocation;
